import { useTranslator } from '@bluebeam/react-i18n';
import { UpdateMultiplePubReq } from 'records/interactions/UpdateMultiplePubReq';
import { PublishRequest, RawPublishRequest } from 'records/PublishRequest';
import * as React from 'react';
import OverlayStdContainer from 'components/Overlays/OverlayStdContainer';
import OverlayHeader from 'components/Overlays/OverlayHeader';
import Label from 'components/inputs/Label';
import SingleSelect from 'components/inputs/dropdowns/SingleSelect.func';
import DateDropdown from 'components/inputs/dropdowns/DateDropdown';
import BaseInput from 'components/inputs/BaseInput';
import OverlayFooter from 'components/Overlays/OverlayFooter';
import { useContext } from 'react';
import AppStoreContext from 'contexts/AppStoreContext';
import { useAppState } from 'hooks/useAppState';
import { useDisciplineSelect } from 'hooks/useDisciplineSelect';

interface Props {
  interaction: UpdateMultiplePubReq;
}

const UpdateMultiplePubReqOverlay: React.FC<Props> = () => {
  const appState = useAppState();
  const { actions } = useContext(AppStoreContext);
  const { disciplineOptions } = useDisciplineSelect();

  const { translate } = useTranslator(['dropdownOptions']);
  const [localPublishRequest, setLocalPublishRequest] =
    React.useState<PublishRequest>(
      new PublishRequest({
        issueddate: Math.ceil(Date.now() / 1000),
      })
    );

  const {
    selectedProject,
    projects,
    selectedPublishRequests,
    publishRequests,
  } = appState;

  const project = projects.get(selectedProject);

  if (!project) {
    return null;
  }

  const handleSave = React.useCallback((): void => {
    const updatedPublishRequests = selectedPublishRequests.reduce(
      (acc, pageId) => {
        const req = publishRequests.get(pageId);
        const d =
          localPublishRequest.discipline.length !== 0
            ? localPublishRequest.discipline
            : req?.discipline;

        const rev =
          localPublishRequest.revisioncomment.length !== 0
            ? localPublishRequest.revisioncomment
            : req?.revisioncomment;
        return acc.mergeIn([pageId], {
          discipline: d,
          issueddate: localPublishRequest.issueddate,
          revisioncomment: rev,
        });
      },
      publishRequests
    );

    actions.setPublishRequests(updatedPublishRequests);

    // Close Overlay
    actions.clearInteractions();
  }, [localPublishRequest, selectedPublishRequests, publishRequests]);

  const handleCancelClick = React.useCallback((): void => {
    actions.clearInteractions();
  }, [actions]);

  const handleChange = React.useCallback(
    (key: keyof RawPublishRequest, val: number | string | null): void => {
      setLocalPublishRequest(localPublishRequest.set(key, val));
    },
    [localPublishRequest]
  );

  const disciplineSelection = React.useMemo(() => {
    return disciplineOptions.has(localPublishRequest.discipline)
      ? disciplineOptions.get(localPublishRequest.discipline)
      : null;
  }, [disciplineOptions, localPublishRequest.discipline]);

  return (
    <OverlayStdContainer
      id="update-multi-pub-req-overlay"
      style={{ width: '336px' }}
    >
      <OverlayHeader
        id="update-multi-pub-req-overlay-header"
        title={`Edit (${selectedPublishRequests.size}) Selected Items`}
        onCancelClick={handleCancelClick}
      />
      <div
        className="column justify-between os"
        style={{
          height: 'calc(100% - 48px)',
        }}
      >
        <div className="m4">
          <div className="mb4">
            <Label label="Discipline" />
            <SingleSelect
              id="update-multi-pub-req-overlay-discipline"
              isClearable
              placeholder={translate('dropdownOptions:chooseDiscipline')}
              value={disciplineSelection}
              options={Array.from(disciplineOptions.values())}
              onChange={(selectedOption): void =>
                handleChange(
                  'discipline',
                  selectedOption.value ? selectedOption.value : ''
                )
              }
            />
          </div>
          <div className="mb4">
            <Label label="Issued Date" />
            <DateDropdown
              id="update-multi-pub-req-overlay-issueddate"
              date={localPublishRequest.issueddate}
              onChange={(date: Date): void => {
                handleChange('issueddate', date.getTime() / 1000);
              }}
            />
          </div>
          <div className="mb4">
            <Label label="Revision Comment" />
            <BaseInput
              id="update-multi-pub-req-overlay-revisioncomment"
              onChange={(evt): void =>
                handleChange('revisioncomment', evt.target.value)
              }
            />
          </div>
        </div>
        <OverlayFooter
          id="update-multi-pub-req-overlay-footer"
          confirmText="Save"
          onCancelClick={handleCancelClick}
          onConfirmClick={handleSave}
        />
      </div>
    </OverlayStdContainer>
  );
};

UpdateMultiplePubReqOverlay.displayName = 'UpdateMultiplePubReqOverlay';

export default UpdateMultiplePubReqOverlay;
